import { useState, useEffect, FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { FormSummaryMessage, InlineFieldError } from '@/components/ui/FormFeedback';
import { SubpageHeader } from '@/components/ui/SubpageHeader';
import { fetchFaqItemList, postContactUs, ApiRequestError, type FaqCategorySection } from '@/api';
import { isValidAustralianPhone, phoneForContactApi } from '@/lib/auPhone';

type ContactFieldErrors = {
  name?: string;
  email?: string;
  phone?: string;
  message?: string;
};

function computeContactFieldErrors(
  name: string,
  email: string,
  phone: string,
  message: string
): ContactFieldErrors {
  const e: ContactFieldErrors = {};
  const nm = name.trim();
  if (!nm) e.name = 'Please enter your name.';
  const em = email.trim();
  if (!em) e.email = 'Please enter your email address.';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(em)) e.email = 'Please enter a valid email address.';
  const ph = phone.trim();
  if (!ph) e.phone = 'Please enter your phone number.';
  else if (!isValidAustralianPhone(ph)) {
    e.phone =
      'Please enter a valid Australian number (e.g. 04XX XXX XXX, 02 XXXX XXXX, or +61 4XX XXX XXX).';
  }
  const msg = message.trim();
  if (!msg) e.message = 'Please enter your message.';
  return e;
}

function firstOpenCompositeKey(sections: FaqCategorySection[]): string | null {
  for (const sec of sections) {
    const first = sec.items[0];
    if (first) return `${sec.category.id}:${first.id}`;
  }
  return null;
}

export default function HelpSupportPage() {
  const navigate = useNavigate();
  const [faqSections, setFaqSections] = useState<FaqCategorySection[]>([]);
  const [faqLoading, setFaqLoading] = useState(true);
  const [faqError, setFaqError] = useState<string | null>(null);
  const [openKey, setOpenKey] = useState<string | null>(null);

  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [fieldErrors, setFieldErrors] = useState<ContactFieldErrors>({});
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setFaqLoading(true);
      setFaqError(null);
      try {
        const sections = await fetchFaqItemList();
        if (cancelled) return;
        setFaqSections(sections);
        setOpenKey(firstOpenCompositeKey(sections));
      } catch (e) {
        if (cancelled) return;
        setFaqSections([]);
        setFaqError(e instanceof ApiRequestError ? e.message : 'Failed to load FAQs.');
      } finally {
        if (!cancelled) setFaqLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    setFormSuccess('');
    const fe = computeContactFieldErrors(form.name, form.email, form.phone, form.message);
    setFieldErrors(fe);
    if (Object.keys(fe).length > 0) {
      requestAnimationFrame(() => {
        if (fe.name) document.getElementById('support-name')?.focus();
        else if (fe.email) document.getElementById('support-email')?.focus();
        else if (fe.phone) document.getElementById('support-phone')?.focus();
        else if (fe.message) document.getElementById('support-message')?.focus();
      });
      return;
    }

    setSending(true);
    try {
      await postContactUs({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: phoneForContactApi(form.phone),
        message: form.message.trim(),
      });
      setForm({ name: '', email: '', phone: '', message: '' });
      setFieldErrors({});
      setFormSuccess('Message sent successfully.');
      window.setTimeout(() => setFormSuccess(''), 4000);
    } catch (err) {
      setFormError(err instanceof ApiRequestError ? err.message : 'Failed to send your message.');
    } finally {
      setSending(false);
    }
  };

  const nameInvalid = !!fieldErrors.name;
  const emailInvalid = !!fieldErrors.email;
  const phoneInvalid = !!fieldErrors.phone;
  const messageInvalid = !!fieldErrors.message;

  return (
    <div className="min-h-screen bg-[#f8f9fa] flex flex-col">
      <SubpageHeader
        title="Help & Support"
        subtitle="FAQs and contact"
        onBack={() => navigate('/me')}
      />

      <div className="flex-1 px-4 py-5 space-y-3">
        {/* FAQ — grouped by category cards */}
        <div className="space-y-3">
          <div className="px-1">
            <h2 className="text-gray-900 font-bold text-sm tracking-tight">Frequently Asked Questions</h2>
            <p className="text-gray-400 text-xs mt-0.5">Browse by topic</p>
          </div>

          {faqLoading && (
            <div className="bg-white rounded-lg border border-gray-200 py-10 flex items-center justify-center gap-2 text-gray-400 text-sm">
              <i className="ri-loader-4-line animate-spin text-lg" />
              Loading…
            </div>
          )}

          {!faqLoading && faqError && (
            <div className="bg-white rounded-lg border border-red-100 px-4 py-2.5 text-xs text-red-600 leading-relaxed">
              {faqError}
            </div>
          )}

          {!faqLoading && !faqError && faqSections.length === 0 && (
            <div className="bg-white rounded-lg border border-gray-200 py-8 text-center text-xs text-gray-400">
              No questions yet.
            </div>
          )}

          {!faqLoading &&
            !faqError &&
            faqSections.length > 0 &&
            faqSections.every((s) => s.items.length === 0) && (
              <div className="bg-white rounded-lg border border-gray-200 py-8 text-center text-xs text-gray-400">
                No questions yet.
              </div>
            )}

          {!faqLoading &&
            !faqError &&
            faqSections.map((section) => (
              <div
                key={section.category.id}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm"
              >
                <div className="px-4 py-3 border-b border-gray-100 bg-gradient-to-r from-gray-50 to-white flex items-center gap-2">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-green-50 text-green-600">
                    <i className="ri-folder-3-line text-base" />
                  </span>
                  <span className="text-gray-900 font-semibold text-sm leading-snug">{section.category.name}</span>
                </div>

                {section.items.length === 0 ? (
                  <div className="px-4 py-5 text-center text-xs text-gray-400">No questions in this category.</div>
                ) : (
                  section.items.map((f, idx) => {
                    const composite = `${section.category.id}:${f.id}`;
                    return (
                      <div
                        key={`${section.category.id}-${f.id}-${idx}`}
                        className={`px-4 ${idx < section.items.length - 1 ? 'border-b border-gray-100' : ''}`}
                      >
                        <button
                          type="button"
                          onClick={() => setOpenKey(openKey === composite ? null : composite)}
                          className="w-full py-2.5 flex items-center justify-between gap-3 cursor-pointer text-left"
                        >
                          <span className="text-gray-800 text-sm font-medium">{f.question}</span>
                          <i
                            className={`${openKey === composite ? 'ri-subtract-line' : 'ri-add-line'} text-gray-400 text-lg flex-shrink-0`}
                          />
                        </button>
                        {openKey === composite && (
                          <div className="pb-3.5 text-gray-500 text-xs leading-relaxed whitespace-pre-wrap">
                            {f.answer}
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            ))}
        </div>

        {/* Contact Form */}
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b border-gray-100">
            <span className="text-gray-800 font-semibold text-sm">Contact Us</span>
          </div>
          <form lang="en" noValidate id="help-support-form" onSubmit={handleSubmit} className="px-4 py-2.5 space-y-3">
            <div>
              <label htmlFor="support-name" className="block text-xs font-normal text-gray-600 mb-1.5">
                Name
              </label>
              <div className="relative">
                <div className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                  <i className="ri-user-3-line text-sm" aria-hidden />
                </div>
                <input
                  id="support-name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  value={form.name}
                  onChange={(ev) => {
                    setForm((p) => ({ ...p, name: ev.target.value }));
                    setFieldErrors((prev) => ({ ...prev, name: undefined }));
                  }}
                  placeholder="Your name"
                  aria-invalid={nameInvalid}
                  aria-describedby={nameInvalid ? 'support-name-error' : undefined}
                  className={`w-full h-8 pl-8 pr-2.5 text-xs font-normal border rounded-md outline-none transition text-gray-800 placeholder:font-normal placeholder:text-xs placeholder:text-gray-400 ${
                    nameInvalid
                      ? 'border-red-300 bg-red-50/35 focus:border-red-400'
                      : 'border-gray-200 bg-gray-50 focus:border-green-500'
                  }`}
                />
              </div>
              <InlineFieldError id="support-name-error" message={fieldErrors.name} />
            </div>

            <div>
              <label htmlFor="support-email" className="block text-xs font-normal text-gray-600 mb-1.5">
                Email
              </label>
              <div className="relative">
                <div className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                  <i className="ri-mail-line text-sm" aria-hidden />
                </div>
                <input
                  id="support-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={form.email}
                  onChange={(ev) => {
                    setForm((p) => ({ ...p, email: ev.target.value }));
                    setFieldErrors((prev) => ({ ...prev, email: undefined }));
                  }}
                  placeholder="you@example.com"
                  aria-invalid={emailInvalid}
                  aria-describedby={emailInvalid ? 'support-email-error' : undefined}
                  className={`w-full h-8 pl-8 pr-2.5 text-xs font-normal border rounded-md outline-none transition text-gray-800 placeholder:font-normal placeholder:text-xs placeholder:text-gray-400 ${
                    emailInvalid
                      ? 'border-red-300 bg-red-50/35 focus:border-red-400'
                      : 'border-gray-200 bg-gray-50 focus:border-green-500'
                  }`}
                />
              </div>
              <InlineFieldError id="support-email-error" message={fieldErrors.email} />
            </div>

            <div>
              <label htmlFor="support-phone" className="block text-xs font-normal text-gray-600 mb-1.5">
                Phone (Australia)
              </label>
              <div className="relative">
                <div className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 flex items-center justify-center text-gray-400">
                  <i className="ri-phone-line text-sm" aria-hidden />
                </div>
                <input
                  id="support-phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  value={form.phone}
                  onChange={(ev) => {
                    setForm((p) => ({ ...p, phone: ev.target.value }));
                    setFieldErrors((prev) => ({ ...prev, phone: undefined }));
                  }}
                  placeholder="04XX XXX XXX or +61 4XX XXX XXX"
                  aria-invalid={phoneInvalid}
                  aria-describedby={phoneInvalid ? 'support-phone-error' : undefined}
                  className={`w-full h-8 pl-8 pr-2.5 text-xs font-normal border rounded-md outline-none transition text-gray-800 placeholder:font-normal placeholder:text-xs placeholder:text-gray-400 ${
                    phoneInvalid
                      ? 'border-red-300 bg-red-50/35 focus:border-red-400'
                      : 'border-gray-200 bg-gray-50 focus:border-green-500'
                  }`}
                />
              </div>
              <InlineFieldError id="support-phone-error" message={fieldErrors.phone} />
            </div>

            <div>
              <label htmlFor="support-message" className="block text-xs font-normal text-gray-600 mb-1.5">
                Message
              </label>
              <div className="relative">
                <div className="absolute left-2.5 top-2.5 w-4 h-4 flex items-center justify-center text-gray-400 pointer-events-none">
                  <i className="ri-chat-3-line text-sm" aria-hidden />
                </div>
                <textarea
                  id="support-message"
                  name="message"
                  value={form.message}
                  onChange={(ev) => {
                    setForm((p) => ({ ...p, message: ev.target.value }));
                    setFieldErrors((prev) => ({ ...prev, message: undefined }));
                  }}
                  rows={4}
                  maxLength={2000}
                  placeholder="Describe your issue or question…"
                  aria-invalid={messageInvalid}
                  aria-describedby={messageInvalid ? 'support-message-error' : undefined}
                  className={`w-full min-h-[6rem] pl-8 pr-2.5 py-2 text-xs font-normal border rounded-md outline-none transition resize-none text-gray-800 placeholder:font-normal placeholder:text-xs placeholder:text-gray-400 ${
                    messageInvalid
                      ? 'border-red-300 bg-red-50/35 focus:border-red-400'
                      : 'border-gray-200 bg-gray-50 focus:border-green-500'
                  }`}
                />
              </div>
              <InlineFieldError id="support-message-error" message={fieldErrors.message} />
            </div>

            <FormSummaryMessage message={formError} />
            {formSuccess ? <FormSummaryMessage message={formSuccess} variant="info" /> : null}

            <button
              type="submit"
              disabled={sending}
              className="w-full h-9 bg-green-600 hover:bg-green-700 active:bg-green-800 text-white text-xs font-medium rounded-lg transition disabled:opacity-60 cursor-pointer whitespace-nowrap flex items-center justify-center gap-1.5"
            >
              {sending ? (
                <span className="flex items-center justify-center gap-1.5">
                  <i className="ri-loader-4-line animate-spin text-sm" />
                  Sending…
                </span>
              ) : (
                <span className="flex items-center justify-center gap-1.5">
                  <i className="ri-send-plane-line text-sm" />
                  Send message
                </span>
              )}
            </button>
          </form>
        </div>
      </div>

    </div>
  );
}